/**
 * features/credit/CreditNoteEditor.tsx
 * Create a credit note against an issued invoice. Picking the invoice
 * prefills the client and its line items, which can be trimmed/edited
 * before issuing.
 */
import { useMemo, useState } from 'react';
import { X, FileMinus, Trash2, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useInvoices } from '@/features/invoices/useInvoices';
import { buildLineItems, computeTotals, formatPula } from '@/lib/money';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import type { CreditNote, VatTreatment } from '@/types';
import { saveDraft, issueCreditNote, deleteDraft } from './creditNotesWrite';

interface LineInput {
  description: string;
  quantity: number;
  unitPrice: number;
  vatTreatment: VatTreatment;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function CreditNoteEditor({ creditNote, onClose }: { creditNote: CreditNote | null; onClose: () => void }) {
  const { user } = useAuth();
  const { currentVatSnapshot } = useSettings();
  const { invoices } = useInvoices();
  const actor = user?.email ?? user?.uid ?? 'unknown';
  const vat = currentVatSnapshot();

  const creditable = useMemo(
    () => invoices.filter((i) => i.number && (i.status === 'issued' || i.status === 'part_paid' || i.status === 'paid')),
    [invoices],
  );

  const [invoiceId, setInvoiceId] = useState(creditNote?.invoiceId ?? '');
  const [issueDate, setIssueDate] = useState(creditNote?.issueDate ?? todayISO());
  const [reason, setReason] = useState(creditNote?.reason ?? '');
  const [lines, setLines] = useState<LineInput[]>(
    creditNote?.lineItems.map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice, vatTreatment: l.vatTreatment })) ?? [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const invoice = invoices.find((i) => i.id === invoiceId);

  function selectInvoice(id: string) {
    setInvoiceId(id);
    const inv = invoices.find((i) => i.id === id);
    if (inv) {
      setLines(inv.lineItems.map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice, vatTreatment: l.vatTreatment })));
    }
  }

  const built = useMemo(() => buildLineItems(lines.filter((l) => l.description.trim()), vat), [lines, vat]);
  const totals = useMemo(() => computeTotals(built), [built]);

  function setLine(idx: number, patch: Partial<LineInput>) {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  async function persist(issue: boolean) {
    if (!invoice) {
      setError('Choose the invoice being credited.');
      return;
    }
    if (built.length === 0) {
      setError('Add at least one line.');
      return;
    }
    setBusy(true);
    setError('');
    const payload = {
      invoiceId: invoice.id,
      invoiceNumber: invoice.number!,
      clientId: invoice.clientId,
      client: invoice.client,
      issueDate,
      reason,
      lineItems: built,
      totals,
      vat,
    };
    try {
      if (issue) {
        if (!window.confirm('Issue this credit note? It will be assigned a number and locked.')) {
          setBusy(false);
          return;
        }
        await issueCreditNote(payload, actor, creditNote?.id);
      } else {
        await saveDraft(payload, actor, creditNote?.id);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
      setBusy(false);
    }
  }

  const isDraft = !creditNote || creditNote.status === 'draft';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-10 w-full max-w-2xl">
        <div className={glassSurface('space-y-4 p-5')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <FileMinus size={18} />
              <h2 className="text-base font-semibold text-white">{creditNote ? `Credit note ${creditNote.number ?? '(draft)'}` : 'New credit note'}</h2>
            </div>
            <button onClick={onClose} className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Against invoice">
              <Select value={invoiceId} onChange={(e) => selectInvoice(e.target.value)} disabled={!isDraft}>
                <option value="">Select an invoice…</option>
                {creditable.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.number} · {i.client.name} · {formatPula(i.totals.total)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Date">
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} disabled={!isDraft} />
            </Field>
          </div>

          {invoice && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">Lines to credit</div>
              {lines.map((l, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input className="flex-1" value={l.description} onChange={(e) => setLine(idx, { description: e.target.value })} placeholder="Description" disabled={!isDraft} />
                  <Input className="w-16 text-right" inputMode="decimal" value={String(l.quantity)} onChange={(e) => setLine(idx, { quantity: Number(e.target.value) || 0 })} disabled={!isDraft} />
                  <Input className="w-28 text-right" inputMode="decimal" value={String(l.unitPrice)} onChange={(e) => setLine(idx, { unitPrice: Number(e.target.value) || 0 })} disabled={!isDraft} />
                  {isDraft && (
                    <button onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))} className="rounded-lg p-1.5 text-gray-400 hover:text-red-600" aria-label="Remove line">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
              {isDraft && (
                <Button variant="ghost" icon={<Plus size={15} />} onClick={() => setLines((ls) => [...ls, { description: '', quantity: 1, unitPrice: 0, vatTreatment: 'standard' }])}>
                  Add line
                </Button>
              )}
            </div>
          )}

          <Field label="Reason">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this invoice is being credited" disabled={!isDraft} />
          </Field>

          <div className="flex items-center justify-between rounded-xl bg-brandSoft px-4 py-2.5 dark:bg-white/5">
            <span className="text-sm font-semibold text-primary dark:text-gray-100">Total to credit</span>
            <span className="text-lg font-bold text-brand">{formatPula(totals.total)}</span>
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex items-center justify-between gap-2">
            <div>
              {creditNote?.status === 'draft' && (
                <Button variant="ghost" icon={<Trash2 size={16} />} onClick={() => { void deleteDraft(creditNote.id); onClose(); }} disabled={busy}>
                  Delete draft
                </Button>
              )}
            </div>
            {isDraft && (
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => void persist(false)} disabled={busy}>
                  Save draft
                </Button>
                <Button onClick={() => void persist(true)} loading={busy}>
                  Issue credit note
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
